import type { TFunction } from 'i18next';
import type { TransportRouteDetailDto } from '../../dto/transport';
import { formatMoneyPl } from '../../utils/currency';
import { allocateLargestRemainder, routeAllocationKeyLabel } from './transportCostSplitPreview';
import { describeRemainingShares } from './transportSaveWarnings';

type RouteForUnlink = Pick<TransportRouteDetailDto, 'direction' | 'routeOrders'>;

export interface UnlinkPreviewShare {
  orderId: string;
  label: string;
  amount: number;
}

const orderLabel = (row: TransportRouteDetailDto['routeOrders'][number]): string => (
  row.order?.displayOrderNumber || row.order?.orderNumber || row.orderId
);

/**
 * „Odepnij zamówienie” (usability round 2026-10-05): what every customer order
 * left on the truck will carry after the unlink, shown BEFORE the operator
 * confirms. Every key splits the truck in proportion to the loads' weights
 * (szt./m²/m³, value, manual amounts) on one percent scale shared with the ZD
 * loads, so taking one load off scales every other share by
 * 1 / (1 − its percent). Rounded to the grosz like the server (largest
 * remainder). The server's split after the unlink is the saved truth; the
 * default key may switch its measure when the removed order was the only one
 * without it — then the success message shows the server's amounts.
 * Empty for an incoming truck (freight goes to batches, not to ZK) and when
 * the shares are hidden from this user.
 */
export function previewSharesAfterUnlink(route: RouteForUnlink | null | undefined, orderId: string): UnlinkPreviewShare[] {
  if (!route || route.direction === 'INBOUND') return [];
  const known = (route.routeOrders || []).filter((row) => (
    row.allocatedTransportCost !== null && row.allocatedTransportCost !== undefined
  ));
  const removed = known.find((row) => row.orderId === orderId);
  const remaining = known.filter((row) => row.orderId !== orderId);
  if (!removed || remaining.length === 0) return [];
  const removedPercent = Number(removed.allocationPercent);
  const remainingSum = remaining.reduce((sum, row) => sum + Number(row.allocatedTransportCost || 0), 0);
  const allPercent = known.reduce((sum, row) => sum + Number(row.allocationPercent), 0);
  const allAmount = known.reduce((sum, row) => sum + Number(row.allocatedTransportCost || 0), 0);
  const remainingPercent = allPercent - removedPercent;
  let total: number;
  if (Number.isFinite(allPercent) && allPercent > 0 && Number.isFinite(removedPercent)
    && removedPercent >= 0 && removedPercent < 100) {
    // The truck's cost in PLN (ZD loads included), to the grosz, then the
    // part the ZK left on it carry after the removed share is gone.
    const truckPLN = Math.round((allAmount / (allPercent / 100)) * 100) / 100;
    total = (truckPLN * remainingPercent) / (100 - removedPercent);
  } else {
    total = remainingSum + Number(removed.allocatedTransportCost || 0);
  }
  // The saved percents carry the basis without the grosz rounding of the
  // amounts (383,33 / 383,34 → 575,00 each, as the server splits).
  const percents = remaining.map((row) => Number(row.allocationPercent));
  const weights = percents.every((value) => Number.isFinite(value) && value >= 0)
    ? percents
    : remaining.map((row) => Math.max(Number(row.allocatedTransportCost || 0), 0));
  const amounts = weights.some((weight) => weight > 0)
    ? allocateLargestRemainder(total, weights)
    : allocateLargestRemainder(total, weights.map(() => 1));
  return remaining.map((row, index) => ({ orderId: row.orderId, label: orderLabel(row), amount: amounts[index] }));
}

/** „2/10/2026 dostanie 1 200,00 PLN, 3/10/2026 dostanie 800,00 PLN” */
export function formatUnlinkPreview(shares: UnlinkPreviewShare[], t: TFunction): string {
  return shares
    .map((share) => t('transport:orderTransports.unlinkPreviewShare', {
      order: share.label,
      amount: formatMoneyPl(share.amount, 'PLN'),
    }))
    .join(', ');
}

/** The confirmation text of „Odepnij zamówienie” with its effect on the money. */
export function buildUnlinkConfirmContent(
  route: RouteForUnlink | null | undefined,
  orderId: string,
  orderName: string,
  t: TFunction,
): string {
  const base = t('transport:orderTransports.confirmUnlinkFromTruck', { order: orderName });
  const preview = formatUnlinkPreview(previewSharesAfterUnlink(route, orderId), t);
  return preview ? `${base} ${t('transport:orderTransports.unlinkPreview', { shares: preview })}` : base;
}

/** Green success after the unlink: the order, the truck and the server's new split. */
export function buildUnlinkSuccessMessage(
  route: (Pick<TransportRouteDetailDto, 'direction' | 'routeOrders' | 'supplierOrders' | 'cargoItems' | 'costAllocationMethod'>) | null,
  orderName: string,
  truckLabel: string,
  t: TFunction,
): string {
  const base = t('transport:orderTransports.unlinkDone', { order: orderName, truck: truckLabel });
  if (!route || route.direction === 'INBOUND') return base;
  const shares = describeRemainingShares(route);
  return shares
    ? `${base} ${t('transport:attach.successSplit', { split: shares, key: routeAllocationKeyLabel(route, t) })}`
    : base;
}
